import { useI18n } from "@/context/I18nContext";
import { Sliders, CheckCircle2, AlertCircle } from "lucide-react";

/**
 * Anatomical comparison of Standard Encysted Artemia vs Decapsulated Artemia.
 *
 * Biological Fact Check:
 * - Decapsulated Artemia is the EXACT SAME cryptobiotic gastrula embryo as the standard cyst.
 * - The only difference is the chemical oxidation removal of the non-digestible tertiary brown
 *   chorion (alveolar and cortical chitinous layers).
 * - Beneath the chorion, both share:
 *   1. The transparent outer and inner embryonic cuticular membrane.
 *   2. The identical golden-amber/orange yolk reserves (lipids, canthaxanthin, trehalose, vitellin).
 *   3. The multicellular cleavage morphology of the dormant gastrula.
 *
 * This component visualizes this 1:1 biological identity with high fidelity, highlighting the
 * dissolution of the opaque outer shell leaving the pure edible embryo intact.
 */
export function AnimatedDecapIllustration() {
  const { t } = useI18n();

  return (
    <div className="glass-card rounded-3xl border border-border/80 bg-background shadow-xl p-6 md:p-8 space-y-6">
      <div className="flex items-center justify-between pb-4 border-b border-border/60">
        <div>
          <span className="text-xs font-bold text-mint-ink uppercase tracking-widest flex items-center gap-1.5 mb-1">
            <Sliders className="h-4 w-4 text-mint-ink" /> {t("visDecap.eyebrow")}
          </span>
          <h3 className="font-display text-xl font-bold text-navy">{t("visDecap.title")}</h3>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6 items-stretch">
        {/* 1. Standard Cyst (Shell-On) */}
        <div className="glass-card rounded-2xl p-5 border border-border bg-surface-alt/60 flex flex-col justify-between space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-navy flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full bg-[#78350f]" />
              {t("visDecap.standard.name")}
            </span>
            <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-primary/10 text-primary font-medium">
              {t("visDecap.standard.badge")}
            </span>
          </div>

          <div className="relative aspect-square max-w-[210px] w-full mx-auto flex items-center justify-center py-2">
            <svg viewBox="0 0 100 100" className="w-full h-full drop-shadow-md">
              <defs>
                {/* 1. Outer Chorion Chitin Shell Radial Gradient */}
                <radialGradient id="chorionShellGrad" cx="35%" cy="32%" r="68%">
                  <stop offset="0%" stopColor="#9a532a" />
                  <stop offset="35%" stopColor="#78350f" />
                  <stop offset="70%" stopColor="#451a03" />
                  <stop offset="100%" stopColor="#270e02" />
                </radialGradient>

                {/* 2. Sub-cortical Alveolar Honeycomb Layer Gradient */}
                <radialGradient id="alveolarGrad" cx="38%" cy="35%" r="62%">
                  <stop offset="0%" stopColor="#b45309" />
                  <stop offset="85%" stopColor="#78350f" />
                  <stop offset="100%" stopColor="#451a03" />
                </radialGradient>

                {/* 3. Embryonic Cuticular Membrane Ring (Shared 1:1) */}
                <radialGradient id="cuticularMembraneGrad" cx="50%" cy="50%" r="50%">
                  <stop offset="80%" stopColor="#fef08a" stopOpacity="0" />
                  <stop offset="94%" stopColor="#fde047" stopOpacity="0.4" />
                  <stop offset="100%" stopColor="#d97706" stopOpacity="0.8" />
                </radialGradient>

                {/* 4. Shared Embryo Core (Golden-Amber Yolk Platelets & Carotenoids) */}
                <radialGradient id="embryoCoreGrad" cx="36%" cy="34%" r="64%">
                  <stop offset="0%" stopColor="#fffbeb" />
                  <stop offset="25%" stopColor="#fef08a" />
                  <stop offset="55%" stopColor="#f59e0b" />
                  <stop offset="82%" stopColor="#d97706" />
                  <stop offset="100%" stopColor="#b45309" />
                </radialGradient>

                {/* 5. Lipid Droplet Sphere Glow (High-density PUFA / Yolk reserves) */}
                <radialGradient id="lipidSphereGlow" cx="28%" cy="28%" r="72%">
                  <stop offset="0%" stopColor="#ffffff" stopOpacity="0.95" />
                  <stop offset="30%" stopColor="#fef08a" stopOpacity="0.9" />
                  <stop offset="65%" stopColor="#fbbf24" stopOpacity="0.8" />
                  <stop offset="100%" stopColor="#d97706" stopOpacity="0" />
                </radialGradient>
              </defs>

              {/* 1. Outer Indigestible Chorion Shell */}
              <circle
                cx="50"
                cy="50"
                r="45"
                fill="url(#chorionShellGrad)"
                stroke="#1f0a02"
                strokeWidth="1.2"
              />

              {/* Cortical micro-pores & fibrous chitin structural texture */}
              <circle
                cx="50"
                cy="50"
                r="41.8"
                fill="none"
                stroke="#3e1707"
                strokeWidth="2.2"
                strokeDasharray="2,3"
                opacity="0.85"
              />
              <circle
                cx="50"
                cy="50"
                r="38.5"
                fill="none"
                stroke="url(#alveolarGrad)"
                strokeWidth="1.4"
                strokeDasharray="1.5,2"
                opacity="0.75"
              />

              {/* 2. Embryonic Cuticular Membrane (Beneath Alveolar Layer) */}
              <circle
                cx="50"
                cy="50"
                r="34"
                fill="none"
                stroke="#fde047"
                strokeWidth="1"
                opacity="0.75"
              />

              {/* 3. Dormant Embryo Core (Gastrula Inside Shell) */}
              <g className="transition-transform duration-500 hover:scale-[1.02] origin-center">
                <circle
                  cx="50"
                  cy="50"
                  r="32.5"
                  fill="url(#embryoCoreGrad)"
                  stroke="#b45309"
                  strokeWidth="0.6"
                />

                {/* Shared High-Density Lipid Spheres */}
                <circle cx="42" cy="42" r="7.5" fill="url(#lipidSphereGlow)" />
                <circle cx="58" cy="45" r="5.5" fill="url(#lipidSphereGlow)" />
                <circle cx="48" cy="58" r="6.5" fill="url(#lipidSphereGlow)" />
                <circle cx="38" cy="54" r="4" fill="url(#lipidSphereGlow)" />
                <circle cx="56" cy="58" r="3.5" fill="url(#lipidSphereGlow)" />
                <circle cx="49" cy="36" r="3" fill="#ffffff" opacity="0.65" />

                {/* Multicellular Gastrula Contours */}
                <path
                  d="M 33 46 Q 44 38 52 46 T 67 52"
                  fill="none"
                  stroke="#b45309"
                  strokeWidth="0.8"
                  opacity="0.4"
                />
                <path
                  d="M 37 57 Q 48 64 61 55"
                  fill="none"
                  stroke="#b45309"
                  strokeWidth="0.8"
                  opacity="0.4"
                />
              </g>

              {/* Anatomical Label: Indigestible Chorion */}
              <rect
                x="14"
                y="81"
                width="72"
                height="13"
                rx="6.5"
                fill="#1f0a02"
                fillOpacity="0.88"
                stroke="#58240c"
                strokeWidth="0.8"
              />
              <text
                x="50"
                y="90"
                fontSize="4.8"
                fontWeight="700"
                fill="#fde68a"
                textAnchor="middle"
                letterSpacing="0.4"
              >
                {t("visDecap.svg.chorion")}
              </text>
            </svg>
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-1.5 text-[11px] font-semibold text-amber-900/80">
              <AlertCircle className="h-3.5 w-3.5 shrink-0 text-amber-700" />
              <span>Chorion requires hatching or separation</span>
            </div>
            <p className="text-[11px] text-muted-foreground leading-relaxed">
              {t("visDecap.standard.body")}
            </p>
          </div>
        </div>

        {/* 2. Decapsulated Artemia (Shell Dissolved, Pure Identical Embryo Core) */}
        <div className="glass-card rounded-2xl p-5 border border-mint/40 bg-mint/5 flex flex-col justify-between space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-navy flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full bg-mint" />
              {t("visDecap.decap.name")}
            </span>
            <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-mint/20 text-navy font-medium">
              {t("visDecap.decap.badge")}
            </span>
          </div>

          <div className="relative aspect-square max-w-[210px] w-full mx-auto flex items-center justify-center py-2">
            <svg viewBox="0 0 100 100" className="w-full h-full drop-shadow-md">
              <defs>
                {/* Clean Translucent Hatchery Membrane Halo */}
                <radialGradient id="decapMembraneHalo" cx="50%" cy="50%" r="50%">
                  <stop offset="65%" stopColor="#10b981" stopOpacity="0" />
                  <stop offset="88%" stopColor="#10b981" stopOpacity="0.15" />
                  <stop offset="100%" stopColor="#059669" stopOpacity="0.3" />
                </radialGradient>
              </defs>

              {/* Gentle Biosecure Boundary Glow */}
              <circle cx="50" cy="50" r="44" fill="url(#decapMembraneHalo)" />

              {/* Transparent Embryonic Cuticular Membrane (Identical layer from standard cyst) */}
              <circle
                cx="50"
                cy="50"
                r="41"
                fill="none"
                stroke="#10b981"
                strokeWidth="1.2"
                strokeDasharray="3,2"
                className="animate-spin"
                style={{ animationDuration: "40s" }}
                opacity="0.8"
              />
              <circle
                cx="50"
                cy="50"
                r="38.5"
                fill="none"
                stroke="#fde047"
                strokeWidth="1"
                strokeDasharray="4,2.5"
                opacity="0.7"
              />

              {/* EXACT SAME Embryo Core as Left (Gastrula, Identical Color & Morphology) */}
              <g className="transition-transform duration-500 hover:scale-[1.02] origin-center">
                <circle
                  cx="50"
                  cy="50"
                  r="36.5"
                  fill="url(#embryoCoreGrad)"
                  stroke="#b45309"
                  strokeWidth="0.7"
                />

                {/* Identical High-Density Lipid Droplets & Yolk Reserves (Matching Left 1:1) */}
                <circle cx="41" cy="41" r="8.5" fill="url(#lipidSphereGlow)" />
                <circle cx="59" cy="44" r="6.5" fill="url(#lipidSphereGlow)" />
                <circle cx="48" cy="59" r="7.5" fill="url(#lipidSphereGlow)" />
                <circle cx="36" cy="55" r="4.8" fill="url(#lipidSphereGlow)" />
                <circle cx="57" cy="59" r="4" fill="url(#lipidSphereGlow)" />
                <circle cx="49" cy="34" r="3.5" fill="#ffffff" opacity="0.65" />

                {/* Identical Gastrula Cellular Cleavage Contours */}
                <path
                  d="M 31 45 Q 43 36 53 45 T 69 52"
                  fill="none"
                  stroke="#b45309"
                  strokeWidth="0.8"
                  opacity="0.4"
                />
                <path
                  d="M 35 58 Q 48 66 63 56"
                  fill="none"
                  stroke="#b45309"
                  strokeWidth="0.8"
                  opacity="0.4"
                />
              </g>

              {/* Anatomical Callout Badge: 0% Shell / 100% Edible Biomass */}
              <rect
                x="14"
                y="81"
                width="72"
                height="13"
                rx="6.5"
                fill="#064e3b"
                fillOpacity="0.9"
                stroke="#10b981"
                strokeWidth="0.8"
              />
              <text
                x="50"
                y="90"
                fontSize="4.8"
                fontWeight="700"
                fill="#ecfdf5"
                textAnchor="middle"
                letterSpacing="0.4"
              >
                SHELL-FREE EMBRYO
              </text>
            </svg>
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-1.5 text-[11px] font-semibold text-emerald-800">
              <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-emerald-600" />
              <span>100% edible embryo biomass (502 kcal/100g)</span>
            </div>
            <p className="text-[11px] text-muted-foreground leading-relaxed">
              {t("visDecap.decap.body")}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
